/* ============================================================================
   CLIPREX storefront logic (Shopify theme build).
   Reads product data from window.CLIPREX_CONFIG (injected by the section via
   Liquid) and powers variant/color selection, the bundle ladder, color-reactive
   theming, and native cart-permalink checkout.

   Gallery behavior:
   - Thumbnails show ONLY shared (non-variant) product images.
   - Selecting a color/hook previews that variant's image for 3s with a
     countdown bar, then reverts to the resting cover/last-picked image.
   - On mobile, selecting a color scrolls up to the product image.
   ============================================================================ */
(function () {
  const CFG = window.CLIPREX_CONFIG || {};

  const PREVIEW_MS = 3000;

  const COLOR_ORDER = ["Red", "Black", "Blue"];
  const colorHex = { Red: "#e0352b", Black: "#1c1c20", Blue: "#2f5fe0" };
  const themes = {
    Red:   { accent: "#e0352b", accent2: "#ff6a4d", soft: "#fdeceb", glow: "224,53,43" },
    Black: { accent: "#1c1c20", accent2: "#54555f", soft: "#eef0f5", glow: "84,85,95"  },
    Blue:  { accent: "#2f5fe0", accent2: "#5b8bff", soft: "#eaf0ff", glow: "47,95,224"  },
  };
  const bundles = [
    { key: "1", name: "Single", units: 1, pct: 0,    badge: "" },
    { key: "2", name: "Duo",    units: 2, pct: 0.10, badge: "Save 10%" },
    { key: "3", name: "Trio",   units: 3, pct: 0.15, badge: "Most popular" },
    { key: "5", name: "Crew",   units: 5, pct: 0.25, badge: "Best value · 25%" },
  ];
  const currencyCode = CFG.currencyCode || "USD";
  let moneyFmt;
  try { moneyFmt = new Intl.NumberFormat(undefined, { style: "currency", currency: currencyCode }); }
  catch (e) { moneyFmt = new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }); }
  const shipDate = CFG.shipDate || "July 11";
  const domain = CFG.domain || "";

  // Build variants[color][hook] = { id, price, img } from the flat Liquid list.
  const variants = {};
  (CFG.variantList || []).forEach((v) => {
    const hook = /without/i.test(v.hook || "") ? "nohook" : "hook";
    (variants[v.color] = variants[v.color] || {})[hook] = {
      id: String(v.id), price: Number(v.price), img: v.image,
    };
  });
  const colors = COLOR_ORDER.filter((c) => variants[c]);
  if (!colors.length) return; // product not found / no variants

  // Set of variant-specific image URLs, so we can keep them OUT of the
  // thumbnail strip (they should only appear when a color is selected).
  const variantImgs = new Set();
  Object.values(variants).forEach((byHook) =>
    Object.values(byHook).forEach((v) => { if (v.img) variantImgs.add(v.img); })
  );
  // Shared gallery = all product images minus the per-variant images.
  const sharedGallery = (CFG.gallery || []).filter((url) => !variantImgs.has(url));

  const state = {
    color: variants.Black ? "Black" : colors[0],
    hook: "hook",
    bundle: "2",
    baseImg: "", // the resting (non-preview) image
  };

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const money = (n) => moneyFmt.format(n || 0);
  const isMobile = () => window.matchMedia("(max-width: 860px)").matches;

  function variant() {
    const byColor = variants[state.color] || {};
    return byColor[state.hook] || byColor.hook || byColor.nohook;
  }
  function unitPrice() { return variant().price; }
  function coverUrl() {
    return sharedGallery[0] || (CFG.gallery && CFG.gallery[0]) || (variant() && variant().img) || "";
  }

  function bundleMath(b) {
    const full = unitPrice() * b.units;
    const total = b.pct ? full * (1 - b.pct) : full;
    return { full, total, perUnit: total / b.units, save: full - total };
  }
  function checkoutUrl() {
    // Volume discounts apply automatically in cart/checkout — no code needed.
    const b = bundles.find((x) => x.key === state.bundle);
    return `${domain}/cart/${variant().id}:${b.units}`;
  }
  function applyTheme() {
    const t = themes[state.color]; if (!t) return;
    const r = document.documentElement;
    r.style.setProperty("--accent", t.accent);
    r.style.setProperty("--accent2", t.accent2);
    r.style.setProperty("--accent-soft", t.soft);
    r.style.setProperty("--glow", t.glow);
  }

  function renderColors() {
    const wrap = $("#colors"); if (!wrap) return;
    wrap.innerHTML = "";
    colors.forEach((c) => {
      const btn = document.createElement("button");
      btn.className = "swatch" + (state.color === c ? " is-active" : "");
      btn.style.setProperty("--sw", colorHex[c] || "#888");
      btn.setAttribute("aria-label", c);
      btn.title = c;
      btn.onclick = () => {
        state.color = c; applyTheme(); renderAll();
        previewVariant();
        if (isMobile()) scrollToGallery();
      };
      wrap.appendChild(btn);
    });
    const cn = $("#colorName"); if (cn) cn.textContent = state.color;
  }
  function renderHook() {
    $$("#hook .seg").forEach((el) =>
      el.classList.toggle("is-active", el.dataset.hook === state.hook));
  }
  function renderBundles() {
    const wrap = $("#bundles"); if (!wrap) return;
    wrap.innerHTML = "";
    bundles.forEach((b) => {
      const m = bundleMath(b);
      const card = document.createElement("button");
      card.className = "bundle" + (state.bundle === b.key ? " is-active" : "") +
        (b.key === "3" ? " is-feature" : "");
      card.onclick = () => { state.bundle = b.key; renderAll(); };
      card.innerHTML =
        (b.badge ? `<span class="bundle__badge">${b.badge}</span>` : "") +
        `<span class="bundle__qty">${b.units} ${b.units > 1 ? "clips" : "clip"}</span>` +
        `<span class="bundle__name">${b.name}</span>` +
        `<span class="bundle__price">${money(m.total)}</span>` +
        `<span class="bundle__per">${money(m.perUnit)} / clip</span>` +
        `<span class="bundle__save${m.save > 0.001 ? "" : " bundle__save--muted"}">` +
        `${m.save > 0.001 ? "You save " + money(m.save) : "&nbsp;"}</span>`;
      wrap.appendChild(card);
    });
  }
  function renderSummary() {
    const b = bundles.find((x) => x.key === state.bundle);
    const m = bundleMath(b);
    const set = (id, txt) => { const el = $(id); if (el) el.textContent = txt; };
    set("#price", money(m.total));
    const show = m.save > 0.001;
    const cmp = $("#compare"), sv = $("#saveTag");
    if (cmp) { cmp.style.display = show ? "" : "none"; cmp.textContent = money(m.full); }
    if (sv) { sv.style.display = show ? "" : "none"; sv.textContent = `Save ${money(m.save)}`; }
    const url = checkoutUrl();
    const main = $("#ctaMain");
    if (main) { main.textContent = `Add to cart · ${money(m.total)}`; main.href = url; }
    const pp = $("#ctaPaypal"); if (pp) pp.href = url;
    const sc = $("#stickyCta"); if (sc) sc.href = url;
    set("#stickyPrice", money(m.total));
  }

  // ---- Gallery / variant preview ------------------------------------------
  let previewTimer = null;
  let frontImg = null;

  function setThumbActive(url) {
    $$("#thumbs .thumb").forEach((t) => t.classList.toggle("is-active", t.dataset.src === url));
  }

  // Crossfade between two stacked layers so a swap never flashes the backdrop.
  function setMainImage(url) {
    const host = $(".gallery__main"); if (!host || !url) return;
    const layers = $$(".gallery__img", host);
    setThumbActive(url);
    if (!layers.length) return;
    if (layers.length < 2) { layers[0].src = url; return; }
    if (!frontImg) frontImg = layers.find((i) => i.classList.contains("is-front")) || layers[0];
    if (frontImg.getAttribute("src") === url) return;
    const back = layers.find((i) => i !== frontImg) || layers[0];
    const reveal = () => {
      back.src = url;
      requestAnimationFrame(() => requestAnimationFrame(() => {
        back.classList.add("is-front");
        frontImg.classList.remove("is-front");
        frontImg = back;
      }));
    };
    const pre = new Image();
    pre.onload = reveal; pre.onerror = reveal;
    pre.src = url;
  }

  function startCountdown() {
    const host = $(".gallery__main"); if (!host) return;
    removeCountdown();
    const bar = document.createElement("div"); bar.className = "cdbar";
    const fill = document.createElement("div"); fill.className = "cdbar__fill";
    bar.appendChild(fill); host.appendChild(bar);
    requestAnimationFrame(() => requestAnimationFrame(() => { fill.style.transform = "scaleX(0)"; }));
  }
  function removeCountdown() {
    const b = $(".gallery__main .cdbar"); if (b) b.remove();
  }
  function cancelPreview() {
    if (previewTimer) { clearTimeout(previewTimer); previewTimer = null; }
    removeCountdown();
  }

  // Show the selected variant's image briefly, then revert to the resting image.
  function previewVariant() {
    const v = variant();
    const url = v && v.img;
    if (!url || url === state.baseImg) { cancelPreview(); setMainImage(state.baseImg); return; }
    cancelPreview();
    setMainImage(url);
    startCountdown();
    previewTimer = setTimeout(() => {
      previewTimer = null;
      removeCountdown();
      setMainImage(state.baseImg);
    }, PREVIEW_MS);
  }

  function renderThumbs() {
    const wrap = $("#thumbs"); if (!wrap) return;
    wrap.innerHTML = "";
    sharedGallery.forEach((url, i) => {
      const t = document.createElement("button");
      t.className = "thumb"; t.dataset.src = url;
      t.innerHTML = `<img src="${url}" alt="CLIPREX view ${i + 1}" loading="lazy">`;
      t.onclick = () => { cancelPreview(); state.baseImg = url; setMainImage(url); };
      wrap.appendChild(t);
    });
  }

  function scrollToGallery() {
    const g = $(".gallery"); if (g) g.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function renderAll() { renderColors(); renderHook(); renderBundles(); renderSummary(); }

  function injectStyles() {
    const style = document.createElement("style");
    style.textContent = `
      .gallery__main { position: relative; }
      .gallery { scroll-margin-top: 84px; }
      .cdbar {
        position: absolute; left: 50%; bottom: 14px; transform: translateX(-50%);
        width: min(62%, 240px); height: 5px; border-radius: 999px;
        background: rgba(0,0,0,.22);
        box-shadow: 0 0 0 1px rgba(255,255,255,.5), 0 1px 6px rgba(0,0,0,.25);
        overflow: hidden; z-index: 7; pointer-events: none;
        opacity: 0; animation: cdbarIn .15s ease forwards;
      }
      @keyframes cdbarIn { to { opacity: 1; } }
      .cdbar__fill {
        width: 100%; height: 100%; border-radius: inherit;
        background: var(--accent, #e0352b);
        transform: scaleX(1); transform-origin: left center;
        transition: transform ${PREVIEW_MS}ms linear;
      }
      @media (prefers-reduced-motion: reduce) { .cdbar { display: none; } }
    `;
    document.head.appendChild(style);
  }

  function init() {
    injectStyles();
    $$("[data-shipdate]").forEach((el) => (el.textContent = shipDate));
    $$("#hook .seg").forEach((el) => {
      el.onclick = () => { state.hook = el.dataset.hook; renderAll(); previewVariant(); };
    });
    renderThumbs();
    applyTheme();
    // Start on a neutral cover image; variant images only appear on selection.
    state.baseImg = coverUrl();
    setMainImage(state.baseImg);
    renderAll();

    $$("[data-pickcolor]").forEach((el) => {
      el.onclick = () => {
        if (!variants[el.dataset.pickcolor]) return;
        state.color = el.dataset.pickcolor;
        applyTheme(); renderAll();
        scrollToGallery();
        previewVariant();
      };
    });

    const buybox = $("#buybox"), sticky = $("#sticky");
    if (buybox && sticky) {
      // Only show once the buy box has scrolled UP out of view (not while it's
      // still below the fold at the top of the page — that would double the CTA).
      new IntersectionObserver(
        ([e]) => sticky.classList.toggle("is-visible", !e.isIntersecting && e.boundingClientRect.top < 0),
        { threshold: 0 }
      ).observe(buybox);
    }
    $$('[data-scroll="buybox"]').forEach((el) => {
      el.onclick = (ev) => { ev.preventDefault(); if (buybox) buybox.scrollIntoView({ behavior: "smooth" }); };
    });
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); } });
    }, { threshold: 0.15 });
    $$(".reveal").forEach((el) => io.observe(el));
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
